import { Inject, Injectable } from '@angular/core';
import { MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { AccountInfo, AuthenticationResult, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface IDToken {
  roles?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class DeclarationsAuthService {
  public loggedIn$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService
  ) {}

  public checkAndSetActiveAccount(): boolean {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (activeAccount) {
      return true;
    } else if (this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
      return true;
    } else {
      return false;
    }
  }

  public setActiveAccount(account: AccountInfo): void {
    this.authService.instance.setActiveAccount(account);
  }

  public handleRedirectObservable$(): Observable<AuthenticationResult | null> {
    return this.authService.handleRedirectObservable();
  }

  public getAllAccounts(): AccountInfo[] {
    return this.authService.instance.getAllAccounts();
  }

  public login$(): Observable<void> {
    this.loggedIn$.next(false);

    if (this.msalGuardConfig.authRequest) {
      return this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      return this.authService.loginRedirect();
    }
  }

  public logout$(): Observable<void> {
    this.loggedIn$.next(false);

    return this.authService.logoutRedirect();
  }

  public hasRole$(role: string): Observable<boolean> {
    return this.loggedIn$.pipe(
      map((loggedIn) => {
        if (!loggedIn) {
          return false;
        }

        const typedIDToken = this.authService.instance.getActiveAccount()?.idTokenClaims as IDToken;
        const hasRole = typedIDToken && typedIDToken.roles && typedIDToken.roles.filter((r) => r === role).length > 0;
        return hasRole !== undefined && hasRole;
      })
    );
  }

  public hasOneOfRoles$(roles: string[]): Observable<boolean> {
    return this.loggedIn$.pipe(
      map((loggedIn) => {
        if (!loggedIn) {
          return false;
        }

        const typedIDToken = this.authService.instance.getActiveAccount()?.idTokenClaims as IDToken;

        if (!typedIDToken || !typedIDToken.roles) {
          return false;
        }

        for (const role of roles) {
          if (typedIDToken.roles.filter((r) => r === role).length > 0) {
            return true;
          }
        }

        return false;
      })
    );
  }

  public getAccessToken$(): Observable<string> {
    return this.authService
      .acquireTokenSilent({
        scopes: environment.consentScopes
      })
      .pipe(map((authResult) => authResult.accessToken));
  }

  public getActiveAccount$(): Observable<AccountInfo | null> {
    return this.loggedIn$.pipe(
      map((loggedIn) => {
        if (!loggedIn) {
          return null;
        }

        return this.authService.instance.getActiveAccount();
      })
    );
  }
}
